import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import { BootService } from '@/services/boot';
import Layout from '@/components/layout/Layout';
import UnaccessibleLayout from '@/components/layout/UnaccessibleLayout';

const MAX_RETRIES = 3;

/**
 * Decides what the app shows, from a single /health-check.
 *
 * Nothing renders until it settles. A 401 or 403 has already triggered a
 * redirect from the axios interceptor by then, so painting the app first would
 * flash authenticated UI and fire a burst of doomed requests on the way out.
 */
const AuthGate: React.FC = () => {
  const { isLoading, isError, error } = useQuery<AxiosResponse, AxiosError<{ errorCode?: string }>>({
    queryKey: ['health-check'],
    queryFn: () => BootService.healthCheck(),
    staleTime: Infinity,
    retry: (failureCount, err) => {
      // an auth failure will not fix itself, and the interceptor is already navigating
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        return false;
      }
      return failureCount < MAX_RETRIES;
    },
  });

  if (isLoading) {
    return null;
  }

  if (isError) {
    const status = error.response?.status;

    // 403: on the way to the portal home — but only a *bare* 403 navigates.
    // A coded one (read-only, and anything else the API tags) is left in place
    // by the interceptor, so it needs an explanation rather than a blank page.
    if (status === 403) {
      return error.response?.data?.errorCode === undefined ? null : (
        <UnaccessibleLayout messageKey="service_error.invalid_token" />
      );
    }

    // 401: on the way to the portal login, unless we have already been there
    // this session and come back to the same answer — that loops, so stop.

    if (status === 401) {
      return BootService.authRedirectAbandoned ? <UnaccessibleLayout messageKey="service_error.invalid_token" /> : null;
    }

    return <UnaccessibleLayout messageKey="service_error.networking" />;
  }

  return <Layout />;
};

export default AuthGate;
